// ═══════════════════════════════════════════════════════════════════════════
// ADMIN MASCOTTE — gabarit de la page (pur)
// Extrait de page-admin-mascot.ts (découpe `renderMascotAdmin`, 2026-09-27) :
// composition PURE du HTML des trois blocs (apparence, paramétrage, phrases)
// depuis les données chargées. Aucun état de closure, aucun accès au DOM — le
// câblage des événements reste dans la page. Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

import { DEFAULT_APPEARANCE } from '../domain/mascot-rig.js';
import type { MascotSettingsData, PhraseData, TriggerCase } from './page-admin-mascot.js';

const HAIR_STYLES = ['bald', 'short', 'wavy', 'bun'];
const GLASSES = ['round', 'square', 'none'];
const FACIALS = ['none', 'mustache', 'goatee', 'beard'];
const NECKS = ['bowtie', 'tie', 'scarf'];

/** HTML complet de la page d'administration de la mascotte : apparence (rig +
 * nom affiché), paramétrage (fréquence, cooldown, style d'affichage) et phrases
 * par cas (système en lecture seule, spécifiques éditables). Les ids et classes
 * (`m-*`) sont le contrat avec le câblage de `renderMascotAdmin`. */
export function buildMascotAdminHtml(
  settings: MascotSettingsData,
  cases: TriggerCase[],
  phrases: PhraseData[],
): string {
  const a = { ...DEFAULT_APPEARANCE, ...(settings.appearance || {}) } as Record<string, string>;
  const sectionStyle =
    'background:var(--bg2);border:1px solid var(--border);border-radius:12px;padding:18px 20px;margin-bottom:18px';
  const titleStyle = 'font-size:16px;font-weight:500;color:var(--navy-light);margin-bottom:14px';
  const labelStyle = 'font-weight:500;font-size:13px;display:block;margin-bottom:4px';
  const fieldStyle =
    'padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg2);font-size:13px';

  const selectField = (id: string, labelKey: string, options: string[], current: string): string =>
    `<div class="form-group" style="margin-bottom:12px">
       <label style="${labelStyle}">${esc(t(labelKey))}</label>
       <select id="${id}" style="${fieldStyle};width:180px">
         ${options
           .map(
             (o) =>
               `<option value="${o}"${o === current ? ' selected' : ''}>${esc(t('mascotAdmin.opt.' + o))}</option>`,
           )
           .join('')}
       </select>
     </div>`;
  const colorField = (id: string, labelKey: string, current: string): string =>
    `<div class="form-group" style="margin-bottom:12px">
       <label style="${labelStyle}">${esc(t(labelKey))}</label>
       <input type="color" id="${id}" value="${esc(current)}" style="width:52px;height:32px;padding:2px;border:1px solid var(--border);border-radius:var(--radius)">
     </div>`;

  const phrasesByCase = (code: string): PhraseData[] =>
    phrases.filter((p) => p.trigger_case === code);

  const phraseRow = (p: PhraseData): string => {
    if (p.is_system) {
      return `<li style="display:flex;gap:8px;align-items:flex-start;padding:8px 0;border-bottom:1px solid var(--border)">
        <span class="badge" style="background:var(--accent-bg);color:var(--accent2);font-size:11px;padding:2px 7px;border-radius:10px;white-space:nowrap;margin-top:2px">${esc(t('mascotAdmin.phrase.system'))}</span>
        <span style="flex:1;font-size:13px;line-height:1.5;color:var(--text2)">${esc(p.text)}</span>
      </li>`;
    }
    return `<li style="display:flex;gap:8px;align-items:flex-start;padding:8px 0;border-bottom:1px solid var(--border)" data-phrase-id="${p.id}">
      <textarea class="m-phrase-text" rows="2" style="flex:1;${fieldStyle};resize:vertical">${esc(p.text)}</textarea>
      <button class="btn btn-secondary btn-sm m-phrase-save" type="button">${esc(t('common.save'))}</button>
      <button class="btn btn-danger btn-sm m-phrase-del" type="button" aria-label="${esc(t('common.delete'))}">🗑</button>
    </li>`;
  };

  const caseBlock = (c: TriggerCase): string =>
    `<div style="margin-bottom:18px">
       <div style="font-weight:500;margin-bottom:6px">${esc(c.label)}</div>
       <ul style="list-style:none;margin:0;padding:0" data-case="${esc(c.code)}">
         ${phrasesByCase(c.code).map(phraseRow).join('') || `<li style="color:var(--text3);font-size:13px;padding:8px 0">${esc(t('mascotAdmin.phrase.none'))}</li>`}
       </ul>
       <div style="display:flex;gap:8px;margin-top:8px">
         <textarea class="m-newphrase" data-case="${esc(c.code)}" rows="2" placeholder="${esc(t('mascotAdmin.phrase.addPlaceholder'))}" style="flex:1;${fieldStyle};resize:vertical"></textarea>
         <button class="btn btn-primary btn-sm m-newphrase-add" data-case="${esc(c.code)}" type="button">${esc(t('mascotAdmin.phrase.add'))}</button>
       </div>
     </div>`;

  return `
    <div style="max-width:820px;padding:8px 0 40px">
      <section style="${sectionStyle}">
        <div style="${titleStyle}">${esc(t('mascotAdmin.section.appearance'))}</div>
        <div style="display:flex;gap:24px;flex-wrap:wrap">
          <div style="flex:0 0 auto"><div id="m-preview" class="orn-mascot" style="display:flex;justify-content:center"></div></div>
          <div style="flex:1 1 380px">
            <div class="form-group" style="margin-bottom:14px">
              <label style="${labelStyle}">${esc(t('mascotAdmin.field.displayName'))}</label>
              <input type="text" id="m-display-name" maxlength="80" value="${esc(settings.display_name)}" style="${fieldStyle};width:100%">
            </div>
            <div style="display:flex;flex-wrap:wrap;gap:0 24px">
              ${selectField('m-hairStyle', 'mascotAdmin.field.hairStyle', HAIR_STYLES, a['hairStyle'] ?? 'bald')}
              ${selectField('m-glasses', 'mascotAdmin.field.glasses', GLASSES, a['glasses'] ?? 'round')}
              ${selectField('m-facial', 'mascotAdmin.field.facial', FACIALS, a['facial'] ?? 'beard')}
              ${selectField('m-neck', 'mascotAdmin.field.neck', NECKS, a['neck'] ?? 'bowtie')}
            </div>
            <div style="display:flex;flex-wrap:wrap;gap:0 20px">
              ${colorField('m-skin', 'mascotAdmin.field.skin', a['skin'] ?? DEFAULT_APPEARANCE.skin)}
              ${colorField('m-hair', 'mascotAdmin.field.hair', a['hair'] ?? DEFAULT_APPEARANCE.hair)}
              ${colorField('m-facialColor', 'mascotAdmin.field.facialColor', a['facialColor'] ?? DEFAULT_APPEARANCE.facialColor)}
              ${colorField('m-accent', 'mascotAdmin.field.accent', a['accent'] ?? DEFAULT_APPEARANCE.accent)}
              ${colorField('m-shirt', 'mascotAdmin.field.shirt', a['shirt'] ?? DEFAULT_APPEARANCE.shirt)}
            </div>
          </div>
        </div>
      </section>

      <section style="${sectionStyle}">
        <div style="${titleStyle}">${esc(t('mascotAdmin.section.tuning'))}</div>
        <div style="display:flex;gap:24px;flex-wrap:wrap">
          <div class="form-group">
            <label style="${labelStyle}">${esc(t('mascotAdmin.field.frequency'))}</label>
            <input type="number" id="m-frequency" min="0" max="100" step="1" value="${esc(String(settings.frequency_percent))}" style="${fieldStyle};width:120px">
            <div style="font-size:12px;color:var(--text3);margin-top:4px;max-width:300px">${esc(t('mascotAdmin.field.frequencyHelp'))}</div>
          </div>
          <div class="form-group">
            <label style="${labelStyle}">${esc(t('mascotAdmin.field.cooldown'))}</label>
            <input type="number" id="m-cooldown" min="0" step="1" value="${esc(String(settings.cooldown_minutes))}" style="${fieldStyle};width:120px">
            <div style="font-size:12px;color:var(--text3);margin-top:4px;max-width:300px">${esc(t('mascotAdmin.field.cooldownHelp'))}</div>
          </div>
          <div class="form-group">
            <label style="${labelStyle}">${esc(t('mascotAdmin.field.displayStyle'))}</label>
            <select id="m-display-style" style="${fieldStyle};width:220px">
              <option value="peek"${settings.display_style !== 'toast' ? ' selected' : ''}>${esc(t('mascotAdmin.opt.peek'))}</option>
              <option value="toast"${settings.display_style === 'toast' ? ' selected' : ''}>${esc(t('mascotAdmin.opt.toast'))}</option>
            </select>
            <div style="font-size:12px;color:var(--text3);margin-top:4px;max-width:300px">${esc(t('mascotAdmin.field.displayStyleHelp'))}</div>
          </div>
        </div>
        <div id="m-settings-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-top:8px"></div>
        <div style="margin-top:8px"><button class="btn btn-primary" id="m-settings-save" type="button">${esc(t('common.save'))}</button></div>
      </section>

      <section style="${sectionStyle}">
        <div style="${titleStyle}">${esc(t('mascotAdmin.section.phrases'))}</div>
        <div style="font-size:13px;color:var(--text3);margin-bottom:14px;line-height:1.5">${esc(t('mascotAdmin.phrases.intro'))}</div>
        ${cases.map(caseBlock).join('')}
      </section>
    </div>
  `;
}
